import { DateTime, ToISODateOptions, ToISOTimeOptions } from "luxon"

import { CiMap } from "./CiMap"
import { isHoliday as isHolidayFn } from "./holiday"

export const DEFAULT_TIME_ZONE = "Europe/Amsterdam"

// See `luxon` documentation for meaning of tokens
// https://moment.github.io/luxon/#/parsing?id=table-of-tokens
const LUXON_DATE_PATTERNS: Array<{ regex: RegExp; format: string }> = [
  // Regular formats
  { regex: /^\d{2}-\d{2}-\d{4}$/, format: "dd-MM-yyyy" },
  { regex: /^\d{1}-\d{2}-\d{4}$/, format: "d-MM-yyyy" },
  { regex: /^\d{1}-\d{1}-\d{4}$/, format: "d-M-yyyy" },
  { regex: /^\d{2}-\d{1}-\d{4}$/, format: "dd-M-yyyy" },

  // Short year format
  { regex: /^\d{1}-\d{1}-\d{2}$/, format: "d-M-yy" },
  { regex: /^\d{2}-\d{1}-\d{2}$/, format: "dd-M-yy" },
  { regex: /^\d{2}-\d{2}-\d{2}$/, format: "dd-MM-yy" },

  // No spacing format
  { regex: /^\d{8}$/, format: "ddMMyyyy" },
  { regex: /^\d{6}$/, format: "ddMMyy" },

  // Reverse format
  { regex: /^\d{4}-\d{2}-\d{2}$/, format: "yyyy-MM-dd" },
  { regex: /^\d{4}-\d{2}-\d{1}$/, format: "yyyy-MM-d" },
  { regex: /^\d{4}-\d{1}-\d{2}$/, format: "yyyy-M-dd" },
  { regex: /^\d{4}-\d{1}-\d{1}$/, format: "yyyy-M-d" },
]

// Conversion map for month names in dates. The keys represent month names in
// Dutch and English and their abbreviations
//
// NOTE IT-2974: We really should *not* be expanding this any further. Instead maybe
// question why these date patterns are provided. We can probably split up the
// use-cases (free text and more structured data)
//
// Library example that does free text date parsing: https://github.com/wanasit/chrono
const MONTH_MAP = CiMap.fromRecord({
  januari: "01",
  jan: "01",
  January: "01",

  februari: "02",
  feb: "02",
  february: "02",

  maart: "03",
  mrt: "03",
  march: "03",
  mar: "03",

  april: "04",
  apr: "04",

  mei: "05",
  may: "05",

  juni: "06",
  jun: "06",
  june: "06",

  juli: "07",
  jul: "07",
  july: "07",

  augustus: "08",
  aug: "08",
  august: "08",

  september: "09",
  sept: "09",
  sep: "09",

  oktober: "10",
  okt: "10",
  october: "10",
  oct: "10",

  november: "11",
  nov: "11",

  december: "12",
  dec: "12",
})

// Regex for month name replacement to avoid reconstructing on each call
const MONTH_REPLACEMENT = new RegExp(`\\b(${Array.from(MONTH_MAP.keys()).join("|")})\\b`, "gi")

// Regex to transform all spaces, hyphens, dots, slashes, etc.
const SEPARATOR_REPLACEMENT = /[\s,.\-+/]+/g

export const luxonDateTime = (date: Date, locale = "nl", timeZone = DEFAULT_TIME_ZONE) => {
  return DateTime.fromJSDate(date).setLocale(locale).setZone(timeZone)
}

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
export const luxonToISO = (date: DateTime, opts?: ToISOTimeOptions) => date.toISO(opts)!

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
export const luxonToISODate = (date: DateTime, opts?: ToISODateOptions) => date.toISODate(opts)!

/** Attempts to parse a date string into a `DateTime` object */
export const getDateStringFromQuery = (
  query: string | undefined = "",
  timeZone = DEFAULT_TIME_ZONE
): DateTime | undefined => {
  const trimmed = String(query || "").trim()

  if (!trimmed.length) {
    return undefined
  }

  // Try to parse the query as an ISO date string. We check if it at least
  // starts with 4 digits, because it assumes the current day for invalid
  // inputs. In those cases we rather fallback to the `LUXON_DATE_PATTERNS`
  if (trimmed.match(/^\d{4}/)) {
    const isIso = DateTime.fromISO(trimmed, { zone: timeZone })
    if (isIso.isValid) {
      return isIso
    }
  }

  // Fallback to parse the query string using patterns
  const potentialDate = trimmed
    .toLowerCase()
    .replace(MONTH_REPLACEMENT, (m) => MONTH_MAP.get(m) ?? "")
    // Convert all to dashes so we can use simpler Luxon formats
    .replace(SEPARATOR_REPLACEMENT, "-")
    .trim()
    // Take first 10 characters to capture only the date/month/year
    .substring(0, 10)

  for (const { regex, format } of LUXON_DATE_PATTERNS) {
    if (regex.test(potentialDate)) {
      const dateObj = DateTime.fromFormat(potentialDate, format, { zone: timeZone })

      if (dateObj.isValid) {
        return dateObj
      }
    }
  }

  return undefined
}

type AfterHoursSummary = {
  isEarlyMorning: boolean
  isEarlyEvening: boolean
  isEvening: boolean
  isWeekend: boolean
  isHoliday: boolean
  isNonWorkingDay: boolean
}

export const afterHoursSummary = (date: DateTime): AfterHoursSummary => {
  const isEarlyEvening = date.hour >= 16 && date.hour < 17
  const isEvening = date.hour >= 17
  const isHoliday = isHolidayFn(date)
  const isWeekend = date.weekday >= 6 || (date.weekday === 5 && isEvening)

  return {
    isEarlyMorning: date.hour < 8,
    isEarlyEvening,
    isEvening,
    isWeekend,
    isHoliday,
    isNonWorkingDay: isHoliday || isWeekend,
  }
}
